import Button from '@components/Button'
import Prompt from '@components/Prompt'
import RainbowTextField from '@components/RainbowTextField/RainbowTextField'
import { useCurrentWallet } from '@plug-core/state'
import { exportKeychain, exportPem } from '@plug-core/handlers/walletActions/wallet'
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { ROUTES } from '@utils/routes'
import saveFile from '@utils/saveFile'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'

import { HiExclamation, HiEyeOff, HiLockOpen, HiSupport } from 'react-icons/hi'
import { useLocation, useNavigate } from 'react-router-dom'

interface AlertItem {
  icon: IconType
  text: string
  color: string
}

const KeyRevealAcceptance = () => {
  const { t } = useTranslation()
  const { palette } = useTheme()
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false)
  const [password, setPassword] = useState<string>('')
  const { currentWallet } = useCurrentWallet()
  const [passwordError, setPasswordError] = useState(false)
  const navigate = useNavigate()

  const { state } = useLocation()

  const items: AlertItem[] = useMemo(() => {
    if (state?.isPem) {
      return [
        {
          text: t('keyRevealAcceptance.pemDownloadAlert'),
          icon: HiExclamation,
          color: palette.rainbow.red,
        },
        {
          text: t('keyRevealAcceptance.accessPem'),
          icon: HiLockOpen,
          color: palette.rainbow.yellow,
        },
        {
          text: t('keyRevealAcceptance.unencryptedPem'),
          icon: HiEyeOff,
          color: palette.rainbow.green,
        },
        {
          text: t('keyRevealAcceptance.pemSupport'),
          icon: HiSupport,
          color: palette.rainbow.purple,
        },
      ]
    }
    return [
      {
        text: t('keyRevealAcceptance.doNotShare'),
        icon: HiExclamation,
        color: palette.rainbow.red,
      },
      {
        text: t('keyRevealAcceptance.hide'),
        icon: HiEyeOff,
        color: palette.rainbow.green,
      },
      {
        text: t('keyRevealAcceptance.access'),
        icon: HiLockOpen,
        color: palette.rainbow.yellow,
      },
      {
        text: t('keyRevealAcceptance.support'),
        icon: HiSupport,
        color: palette.rainbow.purple,
      },
    ]
  }, [t, state])

  const callKeychainExport = useCallback(() => {
    if (state?.isPem) {
      exportPem({ password: password || '', principal: currentWallet })
        .then((pemFile) => {
          saveFile(`${currentWallet}.pem`, pemFile)
          navigate(-1)
        })
        .catch((e) => {
          setPasswordError(true)
        })

      return
    }
    exportKeychain({ password: password || '', principal: currentWallet })
      .then((mnemonic) => {
        navigate(ROUTES.BACKUP_SEED, {
          state: { mnemonic, fromSettings: true },
          replace: true,
        })
      })
      .catch((e) => {
        setPasswordError(true)
      })
  }, [password, currentWallet, state])

  return (
    <>
      <Stack px={2} alignItems="center" spacing={2}>
        <Avatar sx={{ bgcolor: 'background.paper', height: 70, width: 70 }}>
          <HiExclamation color={palette.rainbow.yellow} size={30} />
        </Avatar>
        <Typography variant="h6">{t('keyRevealAcceptance.beforeWeProceed')}</Typography>
        <List>
          {items.map((item, index) => (
            <ListItem key={index}>
              <ListItemAvatar>
                <item.icon color={item.color} size={25} />
              </ListItemAvatar>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
        <Button variant="rainbow" onClick={() => setShowPasswordPrompt(true)} fullWidth>
          {t(`keyRevealAcceptance.${state?.isPem ? 'downloadPem' : 'revealSeedPhrase'}`)}
        </Button>
      </Stack>
      <Prompt show={showPasswordPrompt} close={() => setShowPasswordPrompt(false)}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6">{t('keyRevealAcceptance.passwordTitle')}</Typography>
          <Typography variant="caption" textAlign="center">
            {t('keyRevealAcceptance.passwordDescription')}
          </Typography>
          <Stack width="100%">
            <RainbowTextField
              placeholder={t('common.password')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              error={passwordError}
              onKeyDown={(e) => e.key === 'Enter' && callKeychainExport()}
              autoFocus
              fullWidth
            />
          </Stack>
          <Button variant="rainbow" disabled={!password} onClick={callKeychainExport}>
            {t('common.continue')}
          </Button>
        </Stack>
      </Prompt>
    </>
  )
}

export default KeyRevealAcceptance
